import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useVoicebotWs from "../hooks/useVoicebotWs";
import { createRandomId } from "../utils/random";

const BTN_PRIMARY =
  "inline-flex items-center justify-center gap-2 rounded-xl border border-violet-600/40 bg-violet-600/20 px-3 py-2 text-sm font-semibold hover:bg-violet-600/30 disabled:opacity-60 disabled:cursor-not-allowed";

const BTN =
  "inline-flex items-center justify-center gap-2 rounded-xl border border-[#1f2638] bg-[#121825] px-3 py-2 text-sm font-semibold hover:bg-[#141b2d] disabled:opacity-60 disabled:cursor-not-allowed";

const BTN_DANGER =
  "inline-flex items-center justify-center gap-2 rounded-xl border border-rose-600/40 bg-rose-600/15 px-3 py-2 text-sm font-semibold hover:bg-rose-600/25 disabled:opacity-60 disabled:cursor-not-allowed";

function clean(v) {
  return String(v || "").trim();
}

function joinName(first, last) {
  return [clean(first), clean(last)].filter(Boolean).join(" ").trim();
}

export default function ClientWebCallV2Button({ client }) {
  const [open, setOpen] = useState(false);
  const transcriptListRef = useRef(null);

  const {
    state,
    busy,
    connected,
    sessionStarted,
    micActive,
    transcripts,
    errorMessage,
    warningMessage,
    startSession,
    stopSession,
    clearTranscripts,
  } = useVoicebotWs();

  const clientId = useMemo(() => String(client?._id || client?.id || "").trim(), [client]);
  const canOpen = !!clientId;

  const isThirdPerson = useMemo(
    () => String(client?.contacts?.thirdPerson || "").toLowerCase() === "yes",
    [client]
  );
  const clientName = useMemo(
    () => joinName(client?.personal?.firstName, client?.personal?.lastName),
    [client]
  );
  const thirdPersonName = useMemo(
    () => joinName(client?.contacts?.thirdPersonFirstName, client?.contacts?.thirdPersonLastName),
    [client]
  );
  const effectiveClientName = useMemo(() => {
    if (isThirdPerson && thirdPersonName) return thirdPersonName;
    return clientName;
  }, [clientName, isThirdPerson, thirdPersonName]);
  const statusPill = connected
    ? "border-emerald-600/30 bg-emerald-600/10 text-emerald-200"
    : "border-slate-600/30 bg-slate-600/10 text-slate-200";

  const modePill = micActive
    ? "border-violet-600/30 bg-violet-600/10 text-violet-200"
    : "border-slate-600/30 bg-slate-600/10 text-slate-200";

  const groupedTranscripts = useMemo(() => {
    const out = [];

    for (const line of transcripts) {
      const rawKind = String(line?.kind || "").toLowerCase();
      const kind = rawKind === "bot" ? "bot" : "user";
      const text = String(line?.text || "").trim();
      if (!text) continue;

      const prev = out[out.length - 1];
      if (kind === "bot" && prev?.kind === "bot") {
        prev.text = `${prev.text} ${text}`.trim();
        continue;
      }

      out.push({
        id: line?.id || createRandomId("line"),
        kind,
        text,
      });
    }

    return out;
  }, [transcripts]);

  const start = useCallback(async ({ resetTranscript = true } = {}) => {
    if (!clientId) return;
    if (resetTranscript) clearTranscripts();
    await startSession({
      clientId,
      clientName: effectiveClientName,
      isThirdPerson,
    });
  }, [clearTranscripts, clientId, effectiveClientName, isThirdPerson, startSession]);

  const handleOpen = useCallback(async () => {
    clearTranscripts();
    setOpen(true);
    if (!connected && !busy) {
      await start({ resetTranscript: false });
    }
  }, [busy, clearTranscripts, connected, start]);

  const handleClose = useCallback(async () => {
    await stopSession();
    clearTranscripts();
    setOpen(false);
  }, [clearTranscripts, stopSession]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        void handleClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handleClose, open]);

  useEffect(() => {
    if (!open) return;
    const el = transcriptListRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [open, transcripts.length]);

  const modeLabel = micActive ? "Listening" : sessionStarted ? "Session started" : connected ? "Session only" : "-";

  return (
    <div className="inline-block">
      {!open ? (
        <button
          className={BTN_PRIMARY}
          onClick={handleOpen}
          disabled={busy || !canOpen}
          title={!canOpen ? "Missing client id" : "Web Call"}
        >
          {busy ? "Connecting..." : "Web Call"}
        </button>
      ) : (
        <button className={BTN} onClick={handleClose} disabled={busy}>
          Close
        </button>
      )}

      {open && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center p-4"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              void handleClose();
            }
          }}
          style={{ background: "rgba(0,0,0,0.55)" }}
        >
          <div className="w-full max-w-5xl max-h-[90vh] rounded-3xl border border-[#1f2638] bg-[#0b1020] shadow-2xl flex flex-col overflow-hidden">
            <div className="flex items-center gap-3 border-b border-[#1f2638] px-5 py-4">
              <div className="min-w-0">
                <div className="text-base font-semibold text-slate-200">Обаждане по Web</div>
                <div className="text-xs text-slate-400">
                  {effectiveClientName || "Client"}
                </div>
              </div>

              <div className="flex-1" />

              <button
                className="rounded-xl border border-transparent px-3 py-2 text-slate-300 hover:bg-white/5"
                onClick={handleClose}
                disabled={busy}
                aria-label="Close"
                title="Close"
              >
                x
              </button>
            </div>

            <div className="px-5 py-5 space-y-4 flex-1 min-h-0 flex flex-col">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs ${statusPill}`}>
                  <span className={`h-2 w-2 rounded-full ${connected ? "bg-emerald-400" : "bg-slate-400"}`} />
                  {connected ? "Connected" : "Disconnected"}
                </span>

                <span className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs ${modePill}`}>
                  <span
                    className={[
                      "h-2 w-2 rounded-full",
                      micActive ? "bg-violet-400 animate-pulse" : "bg-slate-400",
                    ].join(" ")}
                  />
                  {modeLabel}
                </span>

                <span className="text-xs text-slate-400">{state}</span>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {!connected ? (
                  <button className={BTN_PRIMARY} onClick={start} disabled={busy || !canOpen}>
                    {busy ? "Свързване..." : "Започни разговор"}
                  </button>
                ) : (
                  <button className={BTN_DANGER} onClick={stopSession} disabled={busy}>
                    Затвори разговора
                  </button>
                )}

                {/* <button className={BTN} onClick={handleClose} disabled={busy}>
                  Затвори прозореца
                </button> */}
              </div>

              <div className="rounded-2xl border border-[#1f2638] bg-[#0e1320]/70 p-4 flex-1 min-h-0">
                <div className="mb-2 text-xs text-slate-400">Транскрипт</div>
                {groupedTranscripts.length === 0 ? (
                  <div className="text-sm text-slate-400">Все още няма транскрипт.</div>
                ) : (
                  <ul
                    ref={transcriptListRef}
                    className="nice-scrollbar h-full max-h-[56vh] space-y-2 overflow-auto pr-1"
                  >
                    {groupedTranscripts.map((line) => {
                      const isBot = String(line?.kind || "").toLowerCase() === "bot";
                      const label = isBot ? "АИ Служител" : "Клиент";
                      const lineClass = isBot
                        ? "border-violet-500/25 bg-violet-600/15"
                        : "border-emerald-500/25 bg-emerald-600/15";
                      const labelClass = isBot ? "text-violet-200" : "text-emerald-200";

                      return (
                        <li
                          key={line.id}
                          className={`rounded-xl border px-3 py-2 text-sm text-slate-100 ${lineClass}`}
                        >
                          <span className={`mb-1 block text-[10px] uppercase tracking-wide ${labelClass}`}>
                            {label}
                          </span>
                          <span className="whitespace-pre-wrap break-words">{line.text}</span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              {warningMessage && (
                <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
                  {warningMessage}
                </div>
              )}

              {errorMessage && (
                <div className="rounded-2xl border border-rose-600/30 bg-rose-600/10 px-4 py-3 text-sm text-rose-200">
                  {errorMessage}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
